import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { IntlProvider } from "react-intl";
import { loadMessages, type MessageCatalog } from "./load-messages.ts";
import { isLocale, resolveInitialLocale, LOCALE_STORAGE_KEY, type Locale } from "./locale.ts";

/** Default display time zone (UTC-3); the per-user setting replaces it once `/me` loads (shell). */
export const DEFAULT_TIME_ZONE = "America/Sao_Paulo";

interface LocaleContextValue {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
  /** IANA time zone for dates; the shell sets it from the user's settings (`GET /me`). */
  readonly timeZone: string;
  readonly setTimeZone: (timeZone: string) => void;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("useLocale must be used inside <I18nProvider>");
  return value;
}

export interface I18nProviderProps {
  readonly children: ReactNode;
  readonly initialLocale?: Locale;
  /**
   * Catalog of `initialLocale`, already loaded (component workshop, tests): the first render is then
   * synchronous instead of empty until the lazy load resolves. Omitted in the app.
   */
  readonly initialMessages?: MessageCatalog;
  /** Injectable for tests. */
  readonly load?: (locale: Locale) => Promise<MessageCatalog>;
}

/** Loads the catalog of the active locale lazily and provides react-intl plus a locale switch. */
export function I18nProvider({
  children,
  initialLocale,
  initialMessages,
  load = loadMessages,
}: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? resolveInitialLocale());
  const [timeZone, setTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [loaded, setLoaded] = useState<{ locale: Locale; messages: MessageCatalog } | undefined>(
    () =>
      initialMessages ? { locale: initialLocale ?? locale, messages: initialMessages } : undefined,
  );

  const skipFirstLoad = useRef(initialMessages !== undefined);

  useEffect(() => {
    // The first catalog is already there (initial messages): only a later locale switch loads.
    if (skipFirstLoad.current) {
      skipFirstLoad.current = false;
      return;
    }
    let cancelled = false;
    void load(locale).then((messages) => {
      if (!cancelled) setLoaded({ locale, messages });
    });
    return () => {
      cancelled = true;
    };
  }, [locale, load]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const context = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale: (next) => {
        if (!isLocale(next)) return;
        window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
        setLocaleState(next);
      },
      timeZone,
      setTimeZone,
    }),
    [locale, timeZone],
  );

  // Until the first catalog is there, render nothing (the page shell is static HTML). On a locale
  // switch the previous catalog stays on screen until the new one loads, so no state is lost.
  if (!loaded) return null;

  return (
    <LocaleContext value={context}>
      <IntlProvider
        locale={loaded.locale}
        messages={loaded.messages}
        timeZone={timeZone}
        defaultLocale="en"
        onError={ignoreMissingTranslation}
      >
        {children}
      </IntlProvider>
    </LocaleContext>
  );
}

// The parity check in CI is what guarantees complete catalogs; a runtime miss renders the id.
function ignoreMissingTranslation(): void {
  /* intentionally silent */
}
